import { Button } from '@appsumo/dorado-react';
import Link from 'next/link';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import CommentList from './sidebar/CommentList';
import DealRatings from './sidebar/DealRatings';
import ProductDetails from './sidebar/ProductDetails';

import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { DISCUSSION } from '~/lib/discussions/constants';
import { DiscussionType } from '~/lib/discussions/types';
import { useEvent } from '~/lib/events';
import { useExperiment } from '~/lib/experiment';
import { PDPContext } from '~/lib/product/context';
import { getPlanTypeText } from '~/lib/product/utils';
import { sentenceCase } from '~/lib/util';
import { LEAVE_REVIEW_PAGE_EXPERIMENT_NAME } from '~/lib/util/constants';
import { PricingCard } from '../product';

export default React.memo(function DiscussionSidebar({
  deal,
  discussionSlug = DISCUSSION.QUESTIONS,
}: Readonly<{
  deal: any;
  discussionSlug?: DiscussionType;
}>) {
  const { userStatus, useReviews2_5UI, useQuestions2_5UI } =
    useContext(PDPContext);
  const { push } = useRouter();
  const [isLeaveReviewPageEnable, setIsLeaveReviewPageEnable] = useState(false);
  const isCommentNewUIEnable =
    discussionSlug === DISCUSSION.REVIEWS ? useReviews2_5UI : useQuestions2_5UI;

  const { variant: leaveReviewVariant } = useExperiment(
    LEAVE_REVIEW_PAGE_EXPERIMENT_NAME,
  );

  useEffect(() => {
    setIsLeaveReviewPageEnable(leaveReviewVariant === 'enabled');
  }, [leaveReviewVariant]);

  const planTypeText = useMemo(
    () => sentenceCase(getPlanTypeText(deal?.unique_plan_types)),
    [deal],
  );

  const submitCTAUrl = useMemo(() => {
    if (discussionSlug === DISCUSSION.REVIEWS && isLeaveReviewPageEnable) {
      return `/products/${deal.slug}/${discussionSlug}/new`;
    }

    return `/products/${deal.slug}/#${discussionSlug}`;
  }, [deal.slug, discussionSlug, isLeaveReviewPageEnable]);

  useEvent('cart:added', () => push('/cart/'));

  useEvent('scroll:pricePlanSection', () => {
    push(`/products/${deal.slug}/#pricePlans`);
  });

  return (
    <aside className="w-[400px] min-w-[400px] pb-8 max-sm:w-full max-sm:min-w-full">
      {!isCommentNewUIEnable && <ProductDetails deal={deal} />}

      {isCommentNewUIEnable && (
        <>
          <PricingCard deal={deal} planTypeText={planTypeText} />

          <Button
            className="mt-6 block text-center"
            tertiary
            href={`/products/${deal.slug}/#pricePlans`}
          >
            <FontAwesomeIcon icon={faExternalLink} className="mr-2" />
            View plan details
          </Button>
        </>
      )}

      {discussionSlug === DISCUSSION.REVIEWS && <DealRatings deal={deal} />}

      <CommentList
        title="Latest questions"
        prefix={`/products/${deal.slug}/`}
        comments={deal.top_5_questions}
      />

      <hr />

      <CommentList
        title="Latest reviews"
        prefix={`/products/${deal.slug}/`}
        comments={deal.top_5_reviews}
      />

      <hr />

      <Link
        href={`/products/${deal.slug}/${discussionSlug}`}
        className="my-4 text-bolt line-clamp-2 hover:underline"
      >
        See all {discussionSlug} for this product
      </Link>

      <Button
        href={submitCTAUrl}
        disabled={
          discussionSlug === DISCUSSION.REVIEWS &&
          !userStatus?.user_has_purchased
        }
        secondary
      >
        {discussionSlug === DISCUSSION.REVIEWS
          ? 'Leave a review'
          : 'Ask a question'}
      </Button>
    </aside>
  );
});
